/**
 * DeviceManagementActions — lifecycle management actions for a gateway device.
 * Re-enter commissioning, deactivate, reactivate, replace, reassign.
 */
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Wifi, RotateCcw, Power, PowerOff, RefreshCw, ArrowRightLeft, Info } from 'lucide-react';
import { useApp } from '@/contexts/AppContext';
import {
  setGatewayLifecycleStatus,
  replaceGateway,
  reassignGateway,
} from '@/services/gateway';
import type { GatewayDevice } from '@/types/gateway';
import { toast } from 'sonner';
import { AddGatewayDialog } from './AddGatewayDialog';

interface DeviceManagementActionsProps {
  device: GatewayDevice;
  propertyName: string;
  onDeviceUpdate: () => void;
}

type ActiveDialog = null | 'recommission' | 'deactivate' | 'replace' | 'reassign';

export function DeviceManagementActions({ device, propertyName, onDeviceUpdate }: DeviceManagementActionsProps) {
  const { properties } = useApp();
  const [activeDialog, setActiveDialog] = useState<ActiveDialog>(null);
  const [isActing, setIsActing] = useState(false);
  const [replaceDialogOpen, setReplaceDialogOpen] = useState(false);
  const [reassignPropertyId, setReassignPropertyId] = useState('');

  // Properties that don't have a gateway already (for reassignment)
  const availableProperties = properties.filter(
    (p) => p.id !== device.property_id
  );

  const handleLifecycleAction = async (nextStatus: string) => {
    setIsActing(true);
    try {
      const result = await setGatewayLifecycleStatus(device.id, nextStatus);
      onDeviceUpdate();
      toast.success(`Gateway status changed to ${result.lifecycle_status}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update gateway status');
    } finally {
      setIsActing(false);
      setActiveDialog(null);
    }
  };

  const handleReplace = async (newClaimCode: string) => {
    setIsActing(true);
    try {
      await replaceGateway(device.id, newClaimCode);
      onDeviceUpdate();
      toast.success('Gateway replaced successfully');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to replace gateway');
    } finally {
      setIsActing(false);
    }
  };

  const handleReassign = async () => {
    if (!reassignPropertyId) return;
    setIsActing(true);
    try {
      await reassignGateway(device.id, reassignPropertyId);
      onDeviceUpdate();
      toast.success('Gateway reassigned. Commissioning required at the new property.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to reassign gateway');
    } finally {
      setIsActing(false);
      setActiveDialog(null);
    }
  };

  const isActive = device.lifecycle_status === 'active';
  const isInactive = device.lifecycle_status === 'inactive';
  const isCommissioning = device.lifecycle_status === 'commissioning';

  return (
    <div className="space-y-4">
      {/* WiFi Recovery Info */}
      <Card className="bg-wt-bg-secondary border-border">
        <CardContent className="py-4 flex items-start gap-3">
          <Info className="w-4 h-4 text-wt-text-secondary mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-wt-text-primary flex items-center gap-1.5">
              <Wifi className="w-3.5 h-3.5" />
              WiFi Recovery
            </p>
            <p className="text-xs text-wt-text-secondary mt-1">
              If the gateway is offline due to WiFi changes, hold the BOOT button while powering on to reconfigure WiFi.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Action buttons */}
      <Card className="bg-wt-bg-secondary border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-wt-text-primary">
            Device Management
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {(isActive || isCommissioning) && (
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start"
              onClick={() => setActiveDialog('recommission')}
              disabled={isActing}
            >
              <RotateCcw className="w-3.5 h-3.5 mr-2" />
              Re-enter Commissioning
            </Button>
          )}

          {isActive && (
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start"
              onClick={() => setActiveDialog('deactivate')}
              disabled={isActing}
            >
              <PowerOff className="w-3.5 h-3.5 mr-2" />
              Deactivate Gateway
            </Button>
          )}

          {isInactive && (
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start"
              onClick={() => handleLifecycleAction('active')}
              disabled={isActing}
            >
              {isActing ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Power className="w-3.5 h-3.5 mr-2" />}
              Reactivate Gateway
            </Button>
          )}

          {(isActive || isInactive) && (
            <>
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={() => setReplaceDialogOpen(true)}
                disabled={isActing}
              >
                <RefreshCw className="w-3.5 h-3.5 mr-2" />
                Replace Gateway
              </Button>

              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={() => setActiveDialog('reassign')}
                disabled={isActing}
              >
                <ArrowRightLeft className="w-3.5 h-3.5 mr-2" />
                Reassign to Different Property
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Re-enter Commissioning dialog */}
      <AlertDialog open={activeDialog === 'recommission'} onOpenChange={() => setActiveDialog(null)}>
        <AlertDialogContent className="bg-wt-bg-secondary border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-wt-text-primary">Re-enter Commissioning</AlertDialogTitle>
            <AlertDialogDescription className="text-wt-text-secondary">
              This will pause alerts and re-validate the bus. Useful after wiring changes or adding equipment. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleLifecycleAction('commissioning')}>
              Re-enter Commissioning
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Deactivate dialog */}
      <AlertDialog open={activeDialog === 'deactivate'} onOpenChange={() => setActiveDialog(null)}>
        <AlertDialogContent className="bg-wt-bg-secondary border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-wt-text-primary">Deactivate Gateway</AlertDialogTitle>
            <AlertDialogDescription className="text-wt-text-secondary">
              This will temporarily disable the gateway. The property will fall back to cloud API monitoring. You can reactivate later without re-provisioning.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleLifecycleAction('inactive')}>
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reassign dialog */}
      <AlertDialog open={activeDialog === 'reassign'} onOpenChange={() => setActiveDialog(null)}>
        <AlertDialogContent className="bg-wt-bg-secondary border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-wt-text-primary">Reassign Gateway</AlertDialogTitle>
            <AlertDialogDescription className="text-wt-text-secondary space-y-2">
              <span className="block">
                The gateway will enter commissioning at the new property. Telemetry from the current site will be ignored until re-commissioning completes at the new location.
              </span>
              <span className="block font-medium text-wt-amber-primary">
                Do not finalize until the device is physically moved.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="px-6 pb-2">
            <Select value={reassignPropertyId} onValueChange={setReassignPropertyId}>
              <SelectTrigger>
                <SelectValue placeholder="Select property..." />
              </SelectTrigger>
              <SelectContent>
                {availableProperties.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <AlertDialogFooter className="px-6 pb-6">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleReassign} disabled={!reassignPropertyId || isActing}>
              {isActing ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
              Reassign
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Replace gateway dialog (reuses AddGatewayDialog scanner) */}
      {replaceDialogOpen && device.property_id && (
        <AddGatewayDialog
          open={replaceDialogOpen}
          onOpenChange={setReplaceDialogOpen}
          propertyId={device.property_id}
          propertyName={propertyName}
        />
      )}
    </div>
  );
}
